import React, { useState } from "react";
import { toast } from "sonner";
import { Send, Upload } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { AGG, aggregate, isNumeric, label, measureOf, unitText, uploadTestFile } from "../../lib/testLibrary";
import { btn } from "../leads/PriorityBadge";

const inp = { textTransform: "none" };

export const ResultForm = ({ a, endpoint, onDone, coach = false, testid = "result-form" }) => {
  const t = a.template;
  const m = measureOf(t);
  const n = t.attempts || 1;
  const [att, setAtt] = useState(Array.from({ length: n }, () => ({ value: "", valid: true })));
  const [f, setF] = useState({ date: new Date().toLocaleDateString("sv-SE"), notes: "", video_url: "", qualitative: "", assessment_score: "", assessment_text: "" });
  const [vids, setVids] = useState([]);
  const [up, setUp] = useState(null);
  const [busy, setBusy] = useState(false);
  const filled = att.filter((x) => x.value !== "");
  const final = isNumeric(m) ? aggregate(filled.filter((x) => x.valid).map((x) => Number(x.value)), t.aggregation || "best", t.direction) : null;
  const needVideo = t.video_requirement === "required" || t.result_type === "video";
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    try { const r = await uploadTestFile(file, "result_video", setUp); setVids((v) => [...v, r]); } catch (err) { toast.error(formatErr(err)); } finally { setUp(null); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(endpoint, {
        date: f.date, attempts: isNumeric(m) ? filled.map((x) => ({ value: Number(x.value), valid: x.valid })) : [],
        qualitative: coach && f.qualitative ? f.qualitative : null, assessment_score: coach && f.assessment_score !== "" ? Number(f.assessment_score) : null,
        assessment_text: coach && f.assessment_text ? f.assessment_text : null, video_file_ids: vids.map((v) => v.id), video_url: f.video_url || null, notes: f.notes || null,
      });
      toast.success(coach ? "Resultado registrado" : "Resultado enviado a tu entrenador"); onDone && onDone();
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={testid} style={{ display: "grid", gap: 10 }}>
      <label style={{ fontSize: 12, color: "#a3a3a3" }}>Fecha
        <input type="date" className="qv-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid={`${testid}-date`} />
      </label>
      {isNumeric(m) && (
        <div>
          <div style={{ fontSize: 12, color: "#a3a3a3", marginBottom: 6 }}>{n > 1 ? `${n} intentos` : "Resultado"} · {unitText(t)}{n > 1 ? ` · se calcula: ${label(AGG, t.aggregation || "best").toLowerCase()}` : ""}</div>
          {att.map((x, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 12, minWidth: 70 }}>{n > 1 ? `Intento ${i + 1}` : "Valor"}</span>
              <input className="qv-input" type="number" inputMode="decimal" step={m === "reps" ? 1 : "any"} min={m === "score" ? t.scale_min : 0} max={m === "score" ? t.scale_max : undefined}
                value={x.value} onChange={(e) => setAtt(att.map((y, j) => (j === i ? { ...y, value: e.target.value } : y)))} style={{ maxWidth: 140 }} data-testid={`${testid}-attempt-${i}`} />
              <span style={{ fontSize: 12, color: "#a3a3a3" }}>{m === "score" ? "pts" : t.unit}</span>
              {n > 1 && <label style={{ fontSize: 12, display: "flex", gap: 4, alignItems: "center" }}><input type="checkbox" checked={x.valid} onChange={(e) => setAtt(att.map((y, j) => (j === i ? { ...y, valid: e.target.checked } : y)))} data-testid={`${testid}-valid-${i}`} /> válido</label>}
            </div>
          ))}
          {n > 1 && <div style={{ fontSize: 13 }} data-testid={`${testid}-final`}>Resultado final: <b>{final ?? "—"} {final !== null ? (m === "score" ? "pts" : t.unit) : ""}</b></div>}
        </div>
      )}
      {coach && t.result_type === "qualitative" && (
        <select className="qv-input" value={f.qualitative} onChange={(e) => setF({ ...f, qualitative: e.target.value })} data-testid={`${testid}-qualitative`}>
          <option value="">— Valoración —</option>{(t.qualitative_options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
      {coach && t.result_type === "combined" && (<>
        {t.qualitative_options?.length > 0 && <select className="qv-input" value={f.qualitative} onChange={(e) => setF({ ...f, qualitative: e.target.value })} data-testid={`${testid}-qualitative`}>
          <option value="">— Valoración técnica —</option>{t.qualitative_options.map((o) => <option key={o} value={o}>{o}</option>)}</select>}
        <textarea className="qv-input" placeholder="Valoración técnica" value={f.assessment_text} onChange={(e) => setF({ ...f, assessment_text: e.target.value })} style={{ ...inp, minHeight: 50 }} data-testid={`${testid}-assessment`} />
      </>)}
      {(t.video_requirement !== "none" || t.result_type === "video") && (
        <div>
          <div style={{ fontSize: 12, color: "#a3a3a3", marginBottom: 6 }}>Vídeo {needVideo ? "(obligatorio)" : "(opcional)"}</div>
          {vids.map((v) => <div key={v.id} style={{ fontSize: 12 }}>✓ {v.name}</div>)}
          <label style={{ ...btn("transparent"), cursor: "pointer", fontSize: 12 }} data-testid={`${testid}-video-upload`}><Upload size={13} /> {up !== null ? `Subiendo ${up}%` : "Subir vídeo"}
            <input type="file" accept="video/*" onChange={upload} style={{ display: "none" }} disabled={up !== null} data-testid={`${testid}-video-file`} />
          </label>
          <input className="qv-input" placeholder="…o pega un enlace" value={f.video_url} onChange={(e) => setF({ ...f, video_url: e.target.value })} style={{ ...inp, marginTop: 6 }} data-testid={`${testid}-video-url`} />
        </div>
      )}
      <textarea className="qv-input" placeholder="Notas (condiciones, sensaciones…)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} style={{ ...inp, minHeight: 50 }} data-testid={`${testid}-notes`} />
      <button style={btn("#dc2626")} onClick={submit} disabled={busy || up !== null || (isNumeric(m) && !filled.length)} data-testid={`${testid}-submit`}><Send size={14} /> {coach ? "Registrar resultado" : "Enviar resultado"}</button>
    </div>
  );
};
